import { FC } from 'react';
import { ElevatorStatus, ReceptionCategory } from 'src/api/eris/models/StatusModel';
import { resolveReceptionIcon, resolveRowStyle } from './ErisStatusResultsTableColumns';
import { TFunction } from 'i18next';
import useGroupService from 'src/api/eris/service/ErisInformationService';
import useDisasterService from 'src/api/eris/service/ErisDisasterService';
import Win95Button from '../../../common/button/Win95Button';

type LegendItemProps = {
  label: string;
  color: string;
};

const LegendItem: FC<LegendItemProps> = ({ label, color }) => {
  return (
    <div className="flex items-center justify-between gap-5">
      <span>{label}</span>
      <div className="flex items-center gap-2">
        <span>:</span>
        <div
          style={{
            backgroundColor: color,
            width: '9rem',
            height: '2rem',
            border: '2px solid #d1d5db',
          }}
        />
      </div>
    </div>
  );
};

export type ErisStatusResultsTableLegendProps = {
  t: TFunction<'translation', undefined>;
};

export const ErisStatusResultsTableLegend = (props: ErisStatusResultsTableLegendProps) => {
  const { prepareGroupStatusCsvDownload } = useGroupService();
  const { getLatestDisaster } = useDisasterService();

  const handleDownload = async () => {
    try {
      const { blob, filename } = await prepareGroupStatusCsvDownload();
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed', err);
    }
    getLatestDisaster();
  };

  const defaultLegendColor = '#ffffff';

  return (
    <div className="flex flex-row items-start gap-4">
      <div className="flex flex-col gap-2">
        <div className="bg-blue-900 px-2 text-white">
          {props.t('eris.supportStatusList.elevatorOperationStatus')}
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 border-2 border-gray-300 p-2">
          <LegendItem
            label={props.t('eris.supportStatusList.recoveryComplete')}
            color={
              resolveRowStyle(ElevatorStatus.recoveryComplete)?.backgroundColor ??
              defaultLegendColor
            }
          />
          <LegendItem
            label={props.t('eris.supportStatusList.communicationInProgress')}
            color={
              resolveRowStyle(ElevatorStatus.communicationInProgress)?.backgroundColor ??
              defaultLegendColor
            }
          />
          <LegendItem
            label={props.t('eris.supportStatusList.suspended')}
            color={resolveRowStyle(ElevatorStatus.suspended)?.backgroundColor ?? defaultLegendColor}
          />
          <LegendItem
            label={props.t('eris.supportStatusList.noStatus')}
            color={resolveRowStyle(ElevatorStatus.noStatus)?.backgroundColor ?? defaultLegendColor}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="bg-blue-900 px-2 text-white">
          {props.t('eris.supportStatusList.reception')}
        </div>
        <div className="flex flex-col gap-2 border-y-2 border-e-2 border-s-2 border-gray-300 p-2">
          <div className="flex flex-row items-center justify-between gap-5">
            {props.t('eris.supportStatusList.helios')}
            {resolveReceptionIcon(ReceptionCategory.helios)}
          </div>
          <div className="flex flex-row items-center justify-between gap-5">
            {props.t('eris.supportStatusList.customerContact')}
            {resolveReceptionIcon(ReceptionCategory.customerContact)}
          </div>
        </div>
      </div>
      <div className="ml-auto flex min-w-fit">
        <div>
          <Win95Button
            title={props.t('eris.common.csvDownload')}
            disabled={false}
            onClick={handleDownload}
          />
        </div>
      </div>
    </div>
  );
};
